import { useEffect } from 'react';

/**
 * Lightweight per-page SEO manager - no external dependency.
 * Sets document.title, meta description, canonical link, OG tags,
 * and optionally injects one or more JSON-LD structured-data scripts.
 * Cleans up injected JSON-LD on unmount so navigation stays sane.
 *
 * `jsonLd` may be a single object OR an array of objects (e.g. Izmir
 * pages ship AutoRepair + FAQPage schemas side by side).
 */
export function useSeo({ title, description, canonical, ogImage, jsonLd, lang = 'en' }) {
  useEffect(() => {
    const prevTitle = document.title;
    if (title) document.title = title;
    if (lang) document.documentElement.setAttribute('lang', lang);

    const upsertMeta = (key, content, attr = 'name') => {
      if (!content) return;
      let el = document.head.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    upsertMeta('description', description);
    upsertMeta('og:title', title, 'property');
    upsertMeta('og:description', description, 'property');
    upsertMeta('og:type', 'website', 'property');
    if (canonical) upsertMeta('og:url', canonical, 'property');
    if (ogImage) upsertMeta('og:image', ogImage, 'property');
    upsertMeta('twitter:card', 'summary_large_image');
    upsertMeta('twitter:title', title);
    upsertMeta('twitter:description', description);

    if (canonical) {
      let link = document.head.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', 'canonical');
        document.head.appendChild(link);
      }
      link.setAttribute('href', canonical);
    }

    const jsonLdList = Array.isArray(jsonLd) ? jsonLd : (jsonLd ? [jsonLd] : []);
    const jsonLdScripts = jsonLdList.map((doc) => {
      const el = document.createElement('script');
      el.type = 'application/ld+json';
      el.setAttribute('data-seo-jsonld', 'true');
      el.text = JSON.stringify(doc);
      document.head.appendChild(el);
      return el;
    });

    return () => {
      document.title = prevTitle;
      jsonLdScripts.forEach((el) => {
        if (el && el.parentNode) el.parentNode.removeChild(el);
      });
    };
  }, [title, description, canonical, ogImage, JSON.stringify(jsonLd), lang]);
}
